import { useState } from "react";
import { Link } from "react-router-dom";

import { PageLoader } from "@/components/ui/Spinner";
import {
  PageHeader,
  SectionCard,
  StatTile,
  Tabs,
} from "@/features/power_projects/components/ui";

import { CashflowSection, DatapointsTable } from "../components";
import { HeadToHeadSection } from "../components_h2h";
import {
  useComparisons,
  useFinAronCashflow,
  useFinAronOverview,
} from "../hooks";
import type { Variant } from "../types";
import {
  fmtPct,
  fmtPln,
  HELP,
  InfoDot,
  marginTone,
  ProgressBar,
  VariantSwitch,
} from "../ui";

type TabKey = "margins" | "cashflow" | "inflows" | "rankings" | "h2h";

export default function FinAronDashboardPage() {
  const [variant, setVariant] = useState<Variant>("neutral");
  const [tab, setTab] = useState<TabKey>("margins");
  const { data, isLoading } = useFinAronOverview(variant);
  const cashflow = useFinAronCashflow(variant);
  const comparisons = useComparisons();

  if (isLoading || !data) return <PageLoader />;
  const { designers, totals } = data.data;
  const meta = data.meta;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <PageHeader
        title="FIN-ARON — panel główny"
        subtitle={
          <>
            Wpływy = wartość projektu w prognozowanej dacie zakończenia (
            {meta.as_of}) · narzut {fmtPct(meta.overhead.narzut_pct)}
            {meta.overhead.overridden ? " (ręczny)" : " (auto)"}
          </>
        }
        actions={<VariantSwitch value={variant} onChange={setVariant} />}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatTile label="Projektanci ZE" value={totals.designers_count} />
        <StatTile label="Aktywne projekty" value={totals.projects_count} />
        <StatTile
          label={`Łączna marża (${variant})`}
          value={fmtPln(totals.total_margin)}
          tone={marginTone(totals.total_margin)}
        />
        <StatTile
          label="Narzut efektywny"
          value={fmtPct(meta.overhead.narzut_pct)}
          hint={
            meta.overhead.auto_pct != null
              ? `auto: ${fmtPct(meta.overhead.auto_pct)}`
              : undefined
          }
          tone="blue"
        />
      </div>

      <Tabs<TabKey>
        tabs={[
          { key: "margins", label: "Marże i rentowność" },
          { key: "cashflow", label: "Cash-flow" },
          {
            key: "inflows",
            label: "Spływy projektów",
            count: cashflow.data?.datapoints.length,
          },
          { key: "rankings", label: "Skuteczność koordynatorów" },
          { key: "h2h", label: "1 vs 1" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "margins" && (
        <SectionCard
          title={`Ranking koordynatorów — wariant ${variant}`}
          bodyClassName="overflow-x-auto"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-[var(--color-border)]">
                <th className="py-2 pr-3">Koordynator</th>
                <th className="py-2 pr-3 text-right">Pensja/mies.</th>
                <th className="py-2 pr-3 text-right">Projekty</th>
                <th className="py-2 pr-3">
                  Postęp śr.
                  <InfoDot text={HELP.progress} />
                </th>
                <th className="py-2 pr-3 text-right">
                  Wpływy
                  <InfoDot text={HELP.inflow} />
                </th>
                <th className="py-2 pr-3 text-right">
                  Podwykonawcy
                  <InfoDot text={HELP.subcontractor} />
                </th>
                <th className="py-2 pr-3 text-right">
                  Pensja + narzut
                  <InfoDot text={HELP.salary} />
                </th>
                <th className="py-2 pr-3 text-right">
                  Marża
                  <InfoDot text={HELP.margin} />
                </th>
                <th className="py-2 pr-3 text-right">
                  Marża %<InfoDot text={HELP.marginPct} />
                </th>
                <th className="py-2 pr-3 text-right">
                  Marża/mies.
                  <InfoDot text={HELP.marginPerMonth} />
                </th>
                <th className="py-2 pr-3 text-right">
                  opt / pes
                  <InfoDot text={HELP.variants} />
                </th>
                <th className="py-2">Flagi</th>
              </tr>
            </thead>
            <tbody>
              {designers.map((d) => (
                <tr
                  key={d.designer_id}
                  className="border-b border-[var(--color-border)] hover:bg-black/5 dark:hover:bg-white/5"
                >
                  <td className="py-2 pr-3">
                    <Link
                      to={`/fin-aron/person/${d.designer_id}`}
                      className="text-sky-600 dark:text-sky-400 hover:underline font-medium"
                    >
                      {d.name}
                    </Link>
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {fmtPln(d.monthly_salary_cost)}
                  </td>
                  <td className="py-2 pr-3 text-right">{d.projects_count}</td>
                  <td className="py-2 pr-3">
                    <ProgressBar pct={d.avg_milestones_pct} />
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {fmtPln(d.total_inflow)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {fmtPln(d.total_subcontractor)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {fmtPln(
                      String(Number(d.total_salary) + Number(d.total_overhead)),
                    )}
                  </td>
                  <td
                    className={`py-2 pr-3 text-right tabular-nums font-semibold ${Number(d.margin) < 0 ? "text-red-600 dark:text-red-400" : "text-emerald-700 dark:text-emerald-400"}`}
                  >
                    {fmtPln(d.margin)}
                  </td>
                  <td
                    className={`py-2 pr-3 text-right tabular-nums ${d.profitability.margin_pct != null && Number(d.profitability.margin_pct) < 0 ? "text-red-600 dark:text-red-400" : ""}`}
                  >
                    {fmtPct(d.profitability.margin_pct)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {fmtPln(d.profitability.margin_per_month)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-xs text-gray-500">
                    {fmtPln(d.margin_by_variant.optimistic)} /{" "}
                    {fmtPln(d.margin_by_variant.pessimistic)}
                  </td>
                  <td className="py-2 text-xs text-amber-600 dark:text-amber-400">
                    {d.flags.join(", ")}
                  </td>
                </tr>
              ))}
              {designers.length === 0 && (
                <tr>
                  <td colSpan={12} className="py-8 text-center text-gray-400">
                    Brak koordynatorów z aktywnymi projektami ZE.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </SectionCard>
      )}

      {tab === "cashflow" &&
        (cashflow.isLoading || !cashflow.data ? (
          <PageLoader />
        ) : (
          <CashflowSection
            events={cashflow.data.events}
            quarters={cashflow.data.quarters}
            storageKey="fin-aron-portfolio-cashflow"
          />
        ))}

      {tab === "inflows" &&
        (cashflow.isLoading || !cashflow.data ? (
          <PageLoader />
        ) : (
          <SectionCard
            title={
              <>
                Kiedy projekty spłyną — termin umowy vs realne prognozy
                <InfoDot text={HELP.inflows} />
              </>
            }
          >
            <DatapointsTable datapoints={cashflow.data.datapoints} />
          </SectionCard>
        ))}

      {tab === "h2h" && (
        <HeadToHeadSection designers={designers} variant={variant} />
      )}

      {tab === "rankings" && (
        <SectionCard
          title="Skuteczność i tempo (okno 30 dni)"
          bodyClassName="overflow-x-auto"
        >
          {comparisons.isLoading || !comparisons.data ? (
            <PageLoader />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-[var(--color-border)]">
                  <th className="py-2 pr-3">Koordynator</th>
                  <th className="py-2 pr-3 text-right">Marża vs mediana</th>
                  <th className="py-2 pr-3 text-right">Na czas (%)</th>
                  <th className="py-2 pr-3 text-right">
                    Poślizg (mediana dni)
                    <InfoDot text={HELP.slip} />
                  </th>
                  <th className="py-2 pr-3 text-right">
                    Kroki/dzień dost.
                    <InfoDot text={HELP.pace} />
                  </th>
                  <th className="py-2 pr-3 text-right">
                    Odbicie (mediana dni)
                    <InfoDot text={HELP.returns} />
                  </th>
                  <th className="py-2 pr-3 text-right">Naruszenia SLA</th>
                  <th className="py-2 pr-3 text-right">Przychód/dzień dost.</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.data.designers.map((r) => (
                  <tr
                    key={r.designer_id}
                    className="border-b border-[var(--color-border)]"
                  >
                    <td className="py-2 pr-3">
                      <Link
                        to={`/fin-aron/person/${r.designer_id}`}
                        className="text-sky-600 dark:text-sky-400 hover:underline"
                      >
                        {r.name}
                      </Link>
                    </td>
                    <td
                      className={`py-2 pr-3 text-right tabular-nums ${r.margin_vs_median != null && r.margin_vs_median < 0 ? "text-red-600 dark:text-red-400" : ""}`}
                    >
                      {r.margin_vs_median != null
                        ? fmtPln(String(r.margin_vs_median))
                        : "—"}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {r.on_track_pct ?? "—"}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {r.forecast_slip_days_median ?? "—"}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {r.done_per_available_day ?? "—"}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {r.returns_median_days ?? "—"}
                    </td>
                    <td className="py-2 pr-3 text-right">{r.sla_breaches}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {r.revenue_per_available_day != null
                        ? fmtPln(r.revenue_per_available_day)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>
      )}
    </div>
  );
}
